import React, { useMemo, useCallback } from 'react';
import { Transfer, ConfigProvider } from 'antd';
import type { Key } from 'react';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand } from '../../base/actionParser';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// 穿梭方向类型
type TransferDirection = 'left' | 'right';

// 穿梭框数据项类型
interface TransferItem {
  key: string;
  title: string;
  description?: string;
  disabled?: boolean;
}

// Semantic DOM 样式类型
interface TransferSemanticStyles {
  root?: React.CSSProperties;
  section?: React.CSSProperties;
  header?: React.CSSProperties;
  title?: React.CSSProperties;
  body?: React.CSSProperties;
  list?: React.CSSProperties;
  item?: React.CSSProperties;
  itemIcon?: React.CSSProperties;
  itemContent?: React.CSSProperties;
  footer?: React.CSSProperties;
  actions?: React.CSSProperties;
}

// 国际化配置
interface TransferLocale {
  itemUnit?: string;
  itemsUnit?: string;
  searchPlaceholder?: string;
  notFoundContent?: React.ReactNode;
}

export interface AndTransferProps extends BaseComponentProps, DataSetConfig {
  /** 数据源（JSON字符串或数组） */
  dataSource?: TransferItem[] | string;
  /** 目标键（JSON字符串或数组） */
  targetKeys?: string[] | string;
  /** 选中键（JSON字符串或数组） */
  selectedKeys?: string[] | string;
  /** 禁用 */
  disabled?: boolean;
  /** 显示搜索框 */
  showSearch?: boolean;
  /** 显示全选 */
  showSelectAll?: boolean;
  /** 单向模式 */
  oneWay?: boolean;
  /** 分页配置 */
  pagination?: boolean | { pageSize: number; simple?: boolean; showSizeChanger?: boolean; showLessItems?: boolean } | string;
  /** 校验状态 */
  status?: '' | 'error' | 'warning';
  /** 标题 */
  titles?: [string, string] | string;
  /** 操作按钮文案 */
  actions?: [string, string] | string;
  /** 国际化配置 */
  locale?: TransferLocale | string;
  /** 穿梭动作 */
  changeAction?: string;
  /** 选择变化动作 */
  selectChangeAction?: string;
  /** 搜索动作 */
  searchAction?: string;
  /** 滚动动作 */
  scrollAction?: string;
  /** 自定义 onChange */
  onChange?: (targetKeys: Key[], direction: TransferDirection, moveKeys: Key[]) => void;
  /** 自定义 onSelectChange */
  onSelectChange?: (sourceSelectedKeys: Key[], targetSelectedKeys: Key[]) => void;
  /** 自定义 onSearch */
  onSearch?: (direction: TransferDirection, value: string) => void;
  /** 自定义 onScroll */
  onScroll?: (direction: TransferDirection, e: React.SyntheticEvent<HTMLUListElement>) => void;
  /** Semantic DOM 样式 */
  styles?: TransferSemanticStyles | string;
  /** Design Token */
  token?: Record<string, any> | string;
  /** 其他 Transfer props */
  [key: string]: any;
}

// 解析 JSON 字符串
const parseJson = <T,>(value: T | string | undefined, fallback: T): T => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
};

export const AndTransfer: React.FC<AndTransferProps> = ({
  id, sessionId, develop, dataSet, dataSetField,
  dataSource: propDataSource,
  targetKeys: propTargetKeys,
  selectedKeys: propSelectedKeys,
  disabled,
  showSearch,
  showSelectAll,
  oneWay,
  pagination: propPagination,
  status,
  titles: propTitles,
  actions: propActions,
  locale: propLocale,
  changeAction,
  selectChangeAction,
  searchAction,
  scrollAction,
  onChange,
  onSelectChange,
  onSearch,
  onScroll,
  styles: propStyles,
  token: propToken,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 解析 props
  const styles = useMemo(() => parseJson(propStyles, {}), [propStyles]);
  const token = useMemo(() => parseJson(propToken, {}), [propToken]);
  const titles = useMemo(() => parseJson(propTitles, ['源列表', '目标列表']), [propTitles]);
  const actions = useMemo(() => parseJson(propActions, ['>', '<']), [propActions]);
  const locale = useMemo(() => parseJson(propLocale, { itemUnit: '项', itemsUnit: '项', searchPlaceholder: '请输入搜索内容' }), [propLocale]);
  const pagination = useMemo(() => parseJson(propPagination, false), [propPagination]);

  // 从 store 获取 targetKeys
  const storeTargetKeys = useMemo(() => {
    if (dataSetKey && dataSetField) {
      const value = getDataSet(dataSetKey, dataSetField);
      if (Array.isArray(value)) return value as string[];
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 从 store 获取数据源
  const storeDataSource = useMemo(() => {
    if (dataSetKey && dataSetField) {
      const data = getDataSet(dataSetKey, `${dataSetField}.dataSource`);
      if (Array.isArray(data)) return data as TransferItem[];
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 设计器模式(develop)：优先 store，无则回退到静态数据；运行时且配置了 dataSet：仅用 store，不回退到静态 dataSource
  const dataSource = useMemo(() => {
    if (develop) return storeDataSource ?? parseJson(propDataSource, []);
    if (dataSetKey) return storeDataSource ?? [];
    return parseJson(propDataSource, []);
  }, [develop, dataSetKey, storeDataSource, propDataSource]);

  // 设计器模式(develop)：优先 store，无则回退到静态数据；运行时且配置了 dataSet：仅用 store，不回退到静态 targetKeys
  const targetKeys = useMemo(() => {
    if (develop) return storeTargetKeys ?? parseJson(propTargetKeys, []);
    if (dataSetKey) return storeTargetKeys ?? [];
    return parseJson(propTargetKeys, []);
  }, [develop, dataSetKey, storeTargetKeys, propTargetKeys]);

  // 解析 selectedKeys
  const selectedKeys = useMemo(() => parseJson(propSelectedKeys, []), [propSelectedKeys]);

  // 穿梭处理
  const handleChange = useCallback((
    nextTargetKeys: Key[],
    direction: TransferDirection,
    moveKeys: Key[]
  ) => {
    if (onChange) {
      onChange(nextTargetKeys, direction, moveKeys);
      return;
    }
    // 更新 store
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, nextTargetKeys);
    }
    // 触发动作
    if (changeAction && sessionId) {
      addCommand(sessionId, createCommand(changeAction, { targetKeys: nextTargetKeys, direction, moveKeys }));
    }
  }, [onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, addCommand]);

  // 选择变化处理
  const handleSelectChange = useCallback((
    sourceSelectedKeys: Key[],
    targetSelectedKeys: Key[]
  ) => {
    if (onSelectChange) {
      onSelectChange(sourceSelectedKeys, targetSelectedKeys);
      return;
    }
    if (selectChangeAction && sessionId) {
      addCommand(sessionId, createCommand(selectChangeAction, { sourceSelectedKeys, targetSelectedKeys }));
    }
  }, [onSelectChange, selectChangeAction, sessionId, addCommand]);

  // 搜索处理
  const handleSearch = useCallback((direction: TransferDirection, value: string) => {
    if (onSearch) {
      onSearch(direction, value);
      return;
    }
    if (searchAction && sessionId) {
      addCommand(sessionId, createCommand(searchAction, { direction, value }));
    }
  }, [onSearch, searchAction, sessionId, addCommand]);

  // 滚动处理
  const handleScroll = useCallback((direction: TransferDirection, e: React.SyntheticEvent<HTMLUListElement>) => {
    if (onScroll) {
      onScroll(direction, e);
      return;
    }
    if (scrollAction && sessionId) {
      addCommand(sessionId, createCommand(scrollAction, { direction }));
    }
  }, [onScroll, scrollAction, sessionId, addCommand]);

  // 渲染 Transfer
  const transferElement = (
    <Transfer<TransferItem>
      dataSource={dataSource}
      targetKeys={targetKeys}
      selectedKeys={selectedKeys}
      disabled={disabled}
      showSearch={showSearch}
      showSelectAll={showSelectAll}
      oneWay={oneWay}
      pagination={pagination}
      status={status || undefined}
      titles={titles}
      actions={actions as React.ReactNode[]}
      locale={locale}
      onChange={handleChange}
      onSelectChange={handleSelectChange}
      onSearch={handleSearch}
      onScroll={handleScroll}
      styles={styles}
      render={(item) => item.title}
      {...restProps}
    />
  );

  // 如果配置了 token，使用 ConfigProvider 包装
  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider theme={{ components: { Transfer: token } }}>
        {transferElement}
      </ConfigProvider>
    );
  }

  return transferElement;
};

export default AndTransfer;
